import { useState } from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  BankingPaymentFilter,
  DataTableFilter,
  FilterType,
  ProjectionKind,
  usePaymentsDataTable,
} from 'generated/resources/datatable.graphql'
import { AvailableCurrencyFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { PaymentInsights } from '../PaymentInsights'
import { Requester } from '../TransfersDataTable/_Requester'
import { Actions } from './_Actions'
import { Amount } from './_Amount'
import { DateLabel } from './_DateLabel'
import { Label } from './_Label'
import { Status } from './_Status'
import { Transfers } from './_Transfers'

export const PaymentsDataTable = ({
  onNewPaymentAction = () => {},
  locale,
  currency,
  availableCurrencies,
}: {
  onNewPaymentAction: () => void
  locale: string
  currency: string
  availableCurrencies: AvailableCurrencyFragment[]
}) => {
  const {
    status: statusSearchParams,
    currency: currencySearchParamValue,
    createdAtFrom,
    createdAtTo,
  } = useSearchParams<{
    status?: string
    currency?: string
    createdAtFrom?: string
    createdAtTo?: string
  }>()
  const [filter, setFilter] = useState('')
  const currencyOptions = availableCurrencies
    .filter((coreCurrency: AvailableCurrencyFragment) => {
      return !!coreCurrency.symbol
    })
    .map((coreCurrency: AvailableCurrencyFragment) => {
      return {
        label: `${coreCurrency.name} (${coreCurrency.isoCode} - ${coreCurrency.symbol})`,
        value: coreCurrency.isoCode,
      }
    })
  const source = usePaymentsDataTable(
    [
      {
        identifier: 'date',
        title: i18n.t('banking.payments.data_table.date'),
        kind: ProjectionKind.Custom,
        hidden: true,
        sortBy: 'date',
        template: '{{ date }}',
        render: ({ date }) => {
          return <DateLabel date={date} />
        },
      },
      {
        identifier: 'due_date',
        title: i18n.t('banking.payments.data_table.due_date'),
        kind: ProjectionKind.Custom,
        sortBy: 'dueDate',
        template: '{{ dueDate }}',
        render: ({ dueDate }) => {
          return <DateLabel date={dueDate} />
        },
      },
      {
        identifier: 'name',
        title: i18n.t('banking.payments.data_table.name'),
        kind: ProjectionKind.Custom,
        sortBy: 'label',
        template: '{{ label }}',
        render: ({ label }) => {
          return <Label label={label} />
        },
      },
      {
        identifier: 'amount',
        title: i18n.t('banking.payments.data_table.amount'),
        kind: ProjectionKind.Custom,
        sortBy: 'amountCents',
        template: '{{ amountCents }}',
        render: (payment) => {
          return <Amount payment={payment} />
        },
      },
      {
        identifier: 'transfers',
        title: i18n.t('banking.payments.data_table.transfers'),
        kind: ProjectionKind.Custom,
        template: '{{ 0 }}',
        render: (payment) => {
          return <Transfers payment={payment} />
        },
      },
      {
        identifier: 'requester',
        title: i18n.t('banking.payments.data_table.created_by'),
        kind: ProjectionKind.Custom,
        sortBy: 'fullName',
        template: '{{ requester.fullName }}',
        render: ({ requester }) => {
          return <Requester requester={requester} />
        },
      },
      {
        identifier: 'status',
        title: i18n.t('banking.payments.data_table.status'),
        kind: ProjectionKind.Custom,
        sortBy: 'status',
        template: '{{ status }}',
        render: (payment) => {
          return <Status status={payment.status} />
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 70,
        render: (payment) => {
          return <Actions payment={payment} />
        },
      },
    ],
    {
      variables: {
        search: filter,
      },
    }
  )

  const filters: DataTableFilter<BankingPaymentFilter>[] = [
    {
      identifier: 'status',
      title: i18n.t('banking.payments.data_table.status'),
      path: 'status',
      default: !!statusSearchParams,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          value: statusSearchParams?.split(','),
          operation: 'in',
          options: [
            {
              // We omit the 'pending_consent' state because it doesn't
              // make sense since we are not integrated with a third-party
              // provider to execute payments
              value: 'init',
              label: i18n.t('banking.payments.statuses.pending'),
            },
            {
              value: 'in_progress',
              label: i18n.t('banking.payments.statuses.in_progress'),
            },
            {
              value: 'rejected',
              label: i18n.t('banking.payments.statuses.failed'),
            },
            {
              value: 'done',
              label: i18n.t('banking.payments.statuses.paid'),
            },
          ],
        },
      ],
    },
    {
      identifier: 'date',
      title: i18n.t('banking.payments.data_table.date'),
      path: 'date',
      default: !!createdAtFrom && !!createdAtTo,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
          value: !!createdAtFrom && !!createdAtTo ? [createdAtFrom, createdAtTo] : undefined,
        },
      ],
    },
    {
      identifier: 'due_date',
      title: i18n.t('banking.payments.data_table.due_date'),
      path: 'dueDate',
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
        },
      ],
    },
    {
      identifier: 'currency',
      title: i18n.t('finance.invoices.list.fields.currency'),
      path: 'currency',
      default: !!currencySearchParamValue,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: currencyOptions,
        },
      ],
    },
  ]

  const EmptyState = (
    <>
      <Icon.Large icon="add-first" color="grey600" />
      <Box flexDirection="column" gap="s24" alignItems="center">
        <Box flexDirection="column" gap="s8" alignItems="center">
          <Text.Header2>{i18n.t('banking.payments.data_table.empty_state.title')}</Text.Header2>
          <Text.Normal>
            {i18n.t('banking.payments.data_table.empty_state.description_2')}
          </Text.Normal>
        </Box>
        <Button type="primary" onClick={onNewPaymentAction}>
          {i18n.t('banking.payments.data_table.empty_state.action')}
        </Button>
      </Box>
    </>
  )

  return (
    <DataTable.Provider
      source={source}
      persistFilters={false}
      pagination={{
        kind: 'offset',
        itemsPerPage: 25,
      }}
      sort={{ path: 'date', direction: 'desc' }}
      filters={filters}
      enableClearFilters
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableExport
          search={{
            value: filter,
            onChange: (ev) => setFilter(ev.target.value),
            placeholder: i18n.t('banking.payments.data_table.search_placeholder'),
          }}
          actions={[
            <Button
              key="newPaymentButton"
              onClick={() => {
                track(trackingEvents.accounting.payments.clickCreateBulkPayment)
                onNewPaymentAction()
              }}
              type="primary"
            >
              {i18n.t('banking.payments.actions.create_bulk_payment')}
            </Button>,
          ]}
        />
        <PaymentInsights defaultCurrency={currency} locale={locale} />
        <DataTable.Table emptyState={EmptyState} autoWidth />
      </Box>
    </DataTable.Provider>
  )
}
